import type { components } from '../../lib/api/schema.gen'

type Sourceable = components['schemas']['Sourceable']
type CatalogItemAvailability = components['schemas']['CatalogItemAvailability']

/**
 * 100% client-side "Recently viewed" history (the revived, per-user-real,
 * client-only alternative to ADR-0128's static curated row — ADR-0127's
 * server-side design was rejected on cost, but the demo now wants real
 * tracking, just kept off the server). One `localStorage` key holds a single
 * versioned envelope, following the same posture `lib/rfq/rfq-bundle-
 * context.tsx` already established for `ff_rfq_bundle_v1` (version + owning
 * identity guard, fail-open read/write) and the try/catch fail-open pattern
 * `verticals/rfq/bulk-cart-warn-preference.ts` uses for a simpler flag.
 *
 * Deliberately snapshots the display data (`sourceable`/`availability`/
 * `category`) AT CAPTURE TIME rather than re-resolving on read — a
 * disclosed reversal of ADR-0127's original "re-resolve, never snapshot"
 * design. The client-side resolve endpoint (`GET /catalog/configurable/
 * {sku}`) only ever returns a bare `Sourceable` (no availability/category),
 * and re-resolving up to 8 tiles on every `/catalog` load would trade a
 * flicker-free static row for a flickering, sometimes-broken one. Storing
 * whatever was on hand at record time is the honest, demo-appropriate trade.
 */
export interface RecentlyViewedEntry {
  readonly catalogSku: string
  readonly viewedAt: string
  readonly sourceable: Sourceable
  /** Always populated by both capture sites (V2 carries a real one off the
   * wire; V1 has none to carry, so it synthesizes a placeholder — see
   * `synthesizePlaceholderAvailability` in `useRecentlyViewed.ts`) — kept
   * required here so every rendered tile/modal always has something to show,
   * matching `CatalogResultCard.availability`'s own required shape. */
  readonly availability: CatalogItemAvailability
  /** Optional, mirroring `CatalogResultCard.category` — a real capture may
   * genuinely have none. */
  readonly category?: string
}

const STORAGE_KEY = 'ff_recently_viewed_v1'
const SCHEMA_VERSION = 1
const MAX_ENTRIES = 8

interface RecentlyViewedEnvelope {
  readonly version: typeof SCHEMA_VERSION
  readonly userId: string
  readonly entries: readonly RecentlyViewedEntry[]
}

function isEntryShape(value: unknown): value is RecentlyViewedEntry {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.catalogSku === 'string' &&
    typeof v.viewedAt === 'string' &&
    typeof v.sourceable === 'object' &&
    v.sourceable !== null &&
    typeof v.availability === 'object' &&
    v.availability !== null
  )
}

/**
 * ADR-0103-style trust guard: version, owning identity (`userId`, the OIDC
 * `sub` off `SessionInfo.userId` — NOT `app_user.id`, which isn't available
 * client-side), and a shallow shape check on every entry. Any mismatch —
 * wrong version, a DIFFERENT user's leftover entry (e.g. a shared machine),
 * or a malformed/foreign payload — is treated identically to "no history,"
 * never partially trusted.
 */
function isEnvelopeForUser(value: unknown, userId: string): value is RecentlyViewedEnvelope {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    v.version === SCHEMA_VERSION &&
    v.userId === userId &&
    Array.isArray(v.entries) &&
    v.entries.every(isEntryShape)
  )
}

type Listener = () => void
const listeners = new Set<Listener>()

/** Cross-instance reactivity seam: capture happens from a DIFFERENT React
 * subtree (e.g. `ConfiguratorDrawer`'s `ConfiguratorCore`, mounted alongside
 * `RecentlyViewed` on the same `/catalog` page) than the row that reads the
 * store, so a plain `useState` in `useRecentlyViewed` would never see a
 * sibling's write. Every successful write/clear notifies; `useRecentlyViewed`
 * subscribes and re-reads. */
export function subscribeRecentlyViewed(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function notify(): void {
  for (const listener of listeners) listener()
}

/** Fail-open on every failure mode (storage disabled/throwing, absent key,
 * unparseable JSON, a shape-mismatched or foreign-user payload) — always
 * degrades to "no history," never throws into the app. */
export function readRecentlyViewed(userId: string): readonly RecentlyViewedEntry[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!isEnvelopeForUser(parsed, userId)) return []
    return parsed.entries
  } catch {
    return []
  }
}

function writeEnvelope(userId: string, entries: readonly RecentlyViewedEntry[]): void {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: SCHEMA_VERSION, userId, entries }),
    )
  } catch {
    // Intentionally swallowed — fail-open, same posture as
    // `bulk-cart-warn-preference.ts`/`rfq-bundle-context.ts`.
  }
  notify()
}

export interface RecordRecentlyViewedInput {
  readonly catalogSku: string
  readonly sourceable: Sourceable
  readonly availability: CatalogItemAvailability
  readonly category?: string
  /** Test-injectable; defaults to `new Date().toISOString()`. */
  readonly viewedAt?: string
}

/**
 * Records one view: dedup key is `catalogSku`, case-insensitive (matching
 * `cart-catalog-lookup.ts`'s `CATALOG_BY_SKU` comparison) — re-viewing an
 * already-present SKU updates its snapshot/`viewedAt` and moves it to most
 * recent rather than growing the list or duplicating a row. Entries are kept
 * MOST-RECENT-FIRST (index 0). Strict LRU cap at {@link MAX_ENTRIES}: adding a
 * genuinely new SKU when already at the cap evicts the single oldest entry
 * (the last element) before inserting.
 */
export function recordRecentlyViewed(userId: string, input: RecordRecentlyViewedInput): void {
  const current = readRecentlyViewed(userId)
  const key = input.catalogSku.toUpperCase()
  const withoutExisting = current.filter((entry) => entry.catalogSku.toUpperCase() !== key)
  const capped =
    withoutExisting.length >= MAX_ENTRIES
      ? withoutExisting.slice(0, MAX_ENTRIES - 1)
      : withoutExisting

  const entry: RecentlyViewedEntry = {
    catalogSku: input.catalogSku,
    viewedAt: input.viewedAt ?? new Date().toISOString(),
    sourceable: input.sourceable,
    availability: input.availability,
    ...(input.category !== undefined ? { category: input.category } : {}),
  }

  writeEnvelope(userId, [entry, ...capped])
}

/** Wipes the whole key outright — called on logout (the user's explicit
 * decision: history does NOT survive a logout, even back into the same
 * account), not merely re-keyed-and-left. Fail-open, same as every other
 * write here. */
export function clearRecentlyViewed(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Intentionally swallowed — see doc comment above.
  }
  notify()
}
